import React, { useEffect, useState } from 'react';
import { Chip, Container, Divider, Stack, Typography } from '@mui/material';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Unstable_Grid2';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';

import InputForm from '../../../components/forms/InputForm';
import SelectPicker from '../../../components/forms/SelectPicker';
import DatePicker from '../../../components/datePickers/DatePicker';
import { useTranslation } from 'react-i18next';
import { useSelector } from '../../../store/reduxHooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { defaultAccountCodes } from '../../../constants/defaultCodes/accountDefaultCodes';
import voucherTypes from '../../../constants/defaultCodes/voucherTypes';
import {
  getVoucherNumber,
  uploadVoucherImage,
} from '../../../controllers/transactions/transactionControllers';
import { getVoucherForEdit } from '../../../controllers/transactions/voucherControllers';
import {
  accountByDefCode,
  bankAndCashAccounts,
} from '../../../controllers/settings/accountControllers';
import { getVendors } from '../../../controllers/settings/partnerControllers';
import {
  getProductItems,
  getProductUnits,
} from '../../../controllers/settings/inventoryControllers';
import { getVendorBalance } from '../../../controllers/reports/partnerReports';
import { getRefNoFromVoucherNo } from '../../../shared/utils/voucherUtils';

import Button from '../../../components/buttons/Button';
import CashOrCreditSwitch from '../components/CashOrCreditSwitch';
import { purchaseNavTitle } from '../../../shared/utils/transactionUtils';
import ItemListHeader from '../components/ItemListHeader';
import PaymentAmount from '../components/PaymentAmount';
import BillOrInvoiceSummary from '../components/BillOrInvoiceSummary';
import { errorToast, successToast } from '../../../constants/snackbar/snackbar';
import {
  editPurchaseExpense,
  purchaseExpense,
} from '../../../controllers/transactions/expenseControllers';
import {
  validateVoucherNo,
  validateBill,
} from '../../../validations/transactions/validateNewBill';
import TransactionItem from '../sales/components/TransactionItem';
import TransactionItemModal from '../sales/components/TransactionItemModal';
import AddNewLine from '../sales/components/AddNewLine';
import TransactionError from '../sales/components/TransactionError';
import ImagePicker from '../../../components/image/ImagePicker';
import { createPDF, getPDFData } from '../../../shared/utils/createPDF';
import { salesAndPurchaseTemplate } from '../../../templates/salesAndPurchaseTemplate';
import { dateFileName } from '../../../shared/utils/getSettingBasedDate';

const defCode = defaultAccountCodes.COST_OF_GOODS_PURCHASED;
const billVoucher = voucherTypes.CREDIT_PURCHASE;
const paymentVoucher = voucherTypes.CASH_PURCHASE;
const initial = { initialData: [] };
const initHPState = { show: false, vendor: true, customer: false };

function PurchaseExpense({ isUpdate = false }) {
  const { authUser } = useSelector(s => s.auth);

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { state: editVoucherId } = useLocation();
  const isEdit = Boolean(editVoucherId);

  if (isUpdate && !editVoucherId) {
    navigate('/journals');
    return null;
  }

  const now = new Date();
  const initState = { voucherDate: now, voucherItems: [], isFullPayment: true };
  const [formData, setData] = useState<any>(initState);
  const [images, setImages] = useState<any>([]);
  const [errors, setErrors] = useState<any>({});
  const [showDTS, setShowDTS] = useState<boolean>(false);
  const [showItemModal, setShowItemModal] = useState<boolean>(false);
  const [activeLineNo, setActiveLineNo] = useState<number>(1);
  const [vendorPayable, setVendorPayable] = useState<any>();

  const [handlePartner, setHandlePartner] = useState<any>({ ...initHPState });
  const [handleProduct, setHandleProduct] = useState<any>({ show: false });
  const [pdfSource, setPdfSource] = useState<any>({ uri: '' });

  const { isFullPayment, paidAmount } = formData;

  // console.log('editVoucherId:', editVoucherId);
  const voucherType = formData.isFullPayment ? paymentVoucher : billVoucher;
  const vNoFunc = () => getVoucherNumber(voucherType);
  const getVNo = { enabled: !isEdit, cacheTime: 0 };

  const vnoQuery = useQuery(['getVoucherNo', voucherType], vNoFunc, getVNo);
  const { data: voucherNumber } = vnoQuery;

  const editFunc = () => getVoucherForEdit(editVoucherId);
  const getEditV = { enabled: isEdit, cacheTime: 0 };
  const editQry = useQuery(['editVoucher', editVoucherId], editFunc, getEditV);
  const { data: editVoucher, isFetched: editTxnFetched } = editQry;

  const eQuery = useQuery(['expAcc', defCode], () => accountByDefCode(defCode));
  const { data: defAccount } = eQuery;

  const vendors = useQuery(['getVendors'], getVendors, initial);

  const prodItemFn = () => getProductItems({ isActive: true });
  const products = useQuery(['products'], prodItemFn, initial);
  const vendorBalanceQuery = useMutation(getVendorBalance);

  const bankAndCashQry = useQuery(['bankAndCash'], bankAndCashAccounts);
  const { data: bankAndCash = [] } = bankAndCashQry;
  const productUnitsQry = useQuery(['productUnits'], getProductUnits);
  const { data: productUnits = [] } = productUnitsQry;

  useEffect(() => {
    if (isEdit) {
      if (editVoucher) {
        const voucherDate = new Date(editVoucher.voucherDate);

        editVoucher.voucherImages && setImages(editVoucher.voucherImages);
        delete editVoucher.voucherImages;
        setData((prev: any) => ({ ...prev, ...editVoucher, voucherDate }));

        if (editVoucher.discountAmount || editVoucher.isTaxable) {
          setShowDTS(true);
        }
      }
    }
  }, [editTxnFetched]);

  useEffect(() => {
    if (voucherNumber) {
      const referenceId = getRefNoFromVoucherNo(voucherNumber);
      setData((prev: any) => ({ ...prev, voucherNumber, referenceId }));
    }
  }, [voucherNumber]);

  useEffect(() => {
    if (isEdit && editVoucher) {
      if (editVoucher.isFullPayment !== isFullPayment) {
        vnoQuery.refetch();
      } else if (editVoucher.isFullPayment === isFullPayment) {
        const { referenceId, voucherNumber: vNo } = editVoucher;
        setData((pr: any) => ({ ...pr, voucherNumber: vNo, referenceId }));
      }
    }
  }, [isFullPayment]);

  const addPurchase = useMutation(purchaseExpense);
  const editPurchase = useMutation(editPurchaseExpense);
  const saveImage = useMutation(uploadVoucherImage);

  const onSubmit = async (generatePDF: boolean) => {
    if (!isFullPayment && paidAmount <= 0 && formData.cashOutAccount) {
      delete formData.cashOutAccount;
    }

    const { isValid, errs } = validateBill(formData, errors);

    let otherValidity: any = { isVoucherNoValid: true };
    !isEdit && (otherValidity = await validateVoucherNo(formData, errs));

    if (isValid && otherValidity.isVoucherNoValid) {
      try {
        if (isUpdate && editVoucherId) {
          // console.log('updating voucher...');

          if (images && images.length && !images[0].id) {
            // console.log('is new image...');

            const imgPaylaod = { images, voucherType: 'purchases' };
            const image = await saveImage.mutateAsync(imgPaylaod);
            formData.image = image.id;
          }

          await editPurchase.mutateAsync(formData);
          queryClient.invalidateQueries(['vouchers']);
          queryClient.invalidateQueries(['getVoucher']);
          queryClient.invalidateQueries(['payBills']);
          queryClient.invalidateQueries(['partyDashledgers']);

          const msg = t(`purchaseExp.updateMsg.success-${isFullPayment}`);
          enqueueSnackbar(msg, successToast);

          // toast.show({ title: msg, duration: 2000 });
        } else {
          // console.log('creating voucher...');
          if (images && images.length) {
            const imgPaylaod = { images, voucherType: 'purchases' };
            const image = await saveImage.mutateAsync(imgPaylaod);

            formData.image = image.id;
          }
          await addPurchase.mutateAsync(formData);

          const msg = t(`purchaseExp.createMsg.success-${isFullPayment}`);
          enqueueSnackbar(msg, successToast);
        }

        queryClient.invalidateQueries(['getDashboardReport']);
        generatePDF && (await onCreatePDF());
        navigate('/journals', { replace: true });
      } catch (err) {
        // console.error('err:', err?.response?.data.message);
        const createErr = t(`purchaseExp.createMsg.error-${isFullPayment}`);
        const updateErr = t(`purchaseExp.updateMsg.error-${isFullPayment}`);
        let title = isEdit ? updateErr : createErr;

        if (err.response?.data?.statusCode === 400) {
          title = err.response?.data?.message;
        }

        enqueueSnackbar(title, errorToast);
      }
    } else {
      isEdit && setErrors(errs);
      !isEdit && setErrors(otherValidity.errss);
      if (!otherValidity.isVoucherNoValid) {
        queryClient.invalidateQueries(['getVoucherNo']);
      }
    }
  };

  const onFormChange = async (name: string, value: any) => {
    setData((prev: any) => ({ ...prev, [name]: value }));

    const { errs } = validateBill({ ...formData, [name]: value }, errors);
    errs[name] ? setErrors({ [name]: errs[name] }) : setErrors({});

    if (name === 'vendor' && value && formData.vendor !== value) {
      const response = await vendorBalanceQuery.mutateAsync(value);
      setVendorPayable({ type: 'CASH_OUT', balance: response });
    } else if (name === 'vendor' && !value) {
      setVendorPayable(null);
    }
  };

  const onFormItemChange = (data: any, lineNo: number) => {
    let myItem = formData.voucherItems.find(item => item.lineNo === lineNo);

    if (!myItem) {
      const vItems = [...formData.voucherItems, { ...data, lineNo }];
      setData(prev => ({ ...prev, voucherItems: vItems }));
      return;
    }

    const vItems = formData.voucherItems.map((item: any) => {
      item.lineNo === lineNo && (item = { ...myItem, ...data });
      return item;
    });

    setData(prev => ({ ...prev, voucherItems: [...vItems] }));
  };

  const onAddNewLine = () => {
    let newLine = formData.voucherItems.length + 1;

    let loopBreaker = 0;
    while (true) {
      const lineNo = formData.voucherItems.find(i => i.lineNo === newLine);
      if (!lineNo) {
        break;
      }
      newLine += 1;
      loopBreaker += 1;
      if (loopBreaker >= 100) {
        break;
      }
    }
    setShowItemModal(true);
    setActiveLineNo(newLine);
  };

  const onRemoveLine = (lineNo: number) => {
    // if (formData.voucherItems.length <= 1) {
    //   return;
    // }

    const voucherItems = formData.voucherItems.filter(
      (item: any) => item.lineNo !== lineNo,
    );
    setData(p => ({ ...p, voucherItems: [...voucherItems] }));
  };

  const getHelpText = (fieldName: string) => {
    if (fieldName in errors) {
      return errors[fieldName];
    }
  };

  const onCreatePDF = async () => {
    const data = {
      voucherType,
      user: authUser.user,
      accounts: [defAccount],
      vendors: vendors.data,
      products: products.data,
    };

    const templateData = getPDFData(formData, data);
    const template = salesAndPurchaseTemplate(templateData);

    const voucherNo = formData.voucherNumber.split('-').join('_');
    const fileName = `${voucherNo}_${dateFileName(
      authUser.user,
    )}`.toLowerCase();

    const source = await createPDF(template, fileName);

    setPdfSource(source);
  };

  // console.log('formData: ', formData);
  // console.log('errors: ', errors);

  const activeItem = formData.voucherItems.find(x => x.lineNo === activeLineNo);

  return (
    <Container>
      <Card sx={{ paddingX: 2 }}>
        <CardContent>
          <Stack pb={1} direction={'row'} justifyContent={'space-between'}>
            <Typography sx={{ fontSize: 20 }} color="text.primary" gutterBottom>
              {purchaseNavTitle(isEdit, isFullPayment)}
            </Typography>

            <CashOrCreditSwitch
              isFullPayment={isFullPayment}
              onToggle={v => onFormChange('isFullPayment', v)}
            />
          </Stack>
          <Divider />

          <Stack py={2} gap={2}>
            <Grid container columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
              <Grid xs={4}>
                <DatePicker
                  entryMinDate={true}
                  label={t('purchaseExp.label.voucherDate')}
                  onDateChange={(v: any) => onFormChange('voucherDate', v)}
                  value={formData.voucherDate}
                />
              </Grid>

              <Grid xs={4}>
                <InputForm
                  label={t('purchaseExp.label.voucherNumber')}
                  isDisabled={true}
                  isRequired={true}
                  isInvalid={'voucherNumber' in errors}
                  helpText={getHelpText('voucherNumber')}
                  defaultValue={formData.voucherNumber}
                />
              </Grid>
              <Grid xs={4}>
                <InputForm
                  defaultValue={formData.referenceId}
                  label={t('purchaseExp.label.referenceId')}
                  isInvalid={'referenceId' in errors}
                  helpText={getHelpText('referenceId')}
                  onChangeText={(v: string) => onFormChange('referenceId', v)}
                />
              </Grid>
            </Grid>
            <Grid container columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
              <Grid xs={12}>
                <SelectPicker
                  isRequired={isFullPayment ? false : true}
                  label={t('purchaseExp.label.vendor')}
                  selectedValue={formData.vendor}
                  selectItems={vendors.data}
                  isInvalid={'vendor' in errors}
                  helpText={'vendor' in errors && errors.vendor}
                  onValueChange={(v: any) => onFormChange('vendor', v)}
                  //  setHandles={setHandlePartner}
                  partnerBalance={vendorPayable && vendorPayable}
                />
              </Grid>
            </Grid>
            <Stack pt={1}>
              <ItemListHeader itemsLen={formData.voucherItems.length} />

              {formData.voucherItems.map((item: any, index) => (
                <TransactionItem
                  key={item.lineNo}
                  index={index + 1}
                  voucherItem={item}
                  productItems={products.data}
                  productUnits={productUnits}
                  onFormValueChange={onFormItemChange}
                  onRemoveLine={onRemoveLine}
                  setHandleProduct={setHandleProduct}
                  isPurchase
                />
              ))}
            </Stack>

            {showItemModal && (
              <TransactionItemModal
                showModal={showItemModal}
                setShowModal={setShowItemModal}
                voucherItem={activeItem || { lineNo: activeLineNo }}
                productItems={products.data || []}
                productUnits={productUnits}
                onFormValueChange={onFormItemChange}
                setHandleProduct={setHandleProduct}
                defaultAccount={defAccount}
                isPurchase
              />
            )}

            <AddNewLine {...{ onAddNewLine, t }} />

            {errors.purchaseAmount && (
              <TransactionError error={errors.purchaseAmount} />
            )}

            <PaymentAmount
              {...{ formData, onFormChange, errors, type: 'PURCHASE', t }}
            />

            {(paidAmount > 0 || isFullPayment) && (
              <SelectPicker
                isRequired={paidAmount || isFullPayment ? true : false}
                selectedValue={formData.cashOutAccount}
                selectItems={bankAndCash}
                label={t('purchaseExp.label.cashOutAccount')}
                placeHolder={t('purchaseExp.label.cashOutAccount')}
                isInvalid={'cashOutAccount' in errors}
                helpText={getHelpText('cashOutAccount')}
                onValueChange={(v: string) => onFormChange('cashOutAccount', v)}
              />
            )}

            <BillOrInvoiceSummary
              t={t}
              voucherItems={formData.voucherItems}
              discountAmount={formData.discountAmount}
              paidAmount={formData.paidAmount}
              isTaxable={formData.isTaxable}
              isFullPayment={formData.isFullPayment}
              isPurchase
            />

            <Stack flexDirection={'row'} gap={3} alignItems={'center'}>
              <Stack flex={1}>
                <InputForm
                  inLineLabel={true}
                  defaultValue={formData.description}
                  label={t('purchaseExp.label.description')}
                  isInvalid={'description' in errors}
                  helpText={'description' in errors && errors.description}
                  onChangeText={(v: string) => onFormChange('description', v)}
                />
              </Stack>
              <Stack pt={0.1}>
                <ImagePicker {...{ images, setImages }} />
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
        <Divider />

        <Stack
          direction="row"
          justifyContent={'flex-end'}
          spacing={4}
          padding={2}>
          <Button
            title={isEdit ? 'Update & Get PDF' : 'Save & Get PDF'}
            onClick={() => onSubmit(true)}
            loading={
              addPurchase.isLoading ||
              editPurchase.isLoading ||
              saveImage.isLoading
            }
          />
          <Button
            title={isEdit ? 'Update Transaction' : 'Save Transaction'}
            onClick={() => onSubmit(false)}
            loading={
              addPurchase.isLoading ||
              editPurchase.isLoading ||
              saveImage.isLoading
            }
          />
        </Stack>
      </Card>
    </Container>
  );
}

export default PurchaseExpense;
